import { useState } from "react";
import { Button } from "@headlessui/react";
import { handleEnterKey } from "../../../../app/keyboardUtils";
import AlertMessage, { AlertLevel } from "../../../../components/AlertMessage";
import {
	DistrictCreateEditModel,
	useAddUserToDistrictMutation,
	useCreateDistrictMutation,
} from "../../districtsApiSlice";
import PrimaryDistrictInfoSection from "../sections/PrimaryDistrictInfoSection";
import DistrictAddressInfoSection from "../sections/DistrictAddressInfoSection";

interface AddDistrictProps {
	userIdToAdd?: number;
	saveButtonText?: string;
	onClose: () => void;
}

const initialModel: DistrictCreateEditModel = {
	id: 0,
	name: "",
	state: "",
	address: "",
	city: "",
	zip: "",
	phone: "",
	code: "",
	emailAddress: "",
	externalID: "",
	cleverID: "",
	showCleverId: false,
	classLinkID: "",
	showClassLinkId: false,
	disableUserImages: false,
	isIncludedInReports: true,
	takenSeats: 0,
	totalSeats: 0,
};

export default function AddDistrictForm(props: AddDistrictProps) {
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [model, setModel] = useState<DistrictCreateEditModel>(initialModel);
	const [showAddressSection, setShowAddressSection] = useState(true);

	function toggleShowAddressSection() {
		setShowAddressSection((val) => !val);
	}

	const [addDistrict] = useCreateDistrictMutation();
	const [addUserToDistrict] = useAddUserToDistrictMutation();

	const setPrimaryInfo = (
		patch: Partial<Pick<DistrictCreateEditModel, "name" | "code" | "externalID">>
	) => setModel((s) => ({ ...s, ...patch }));

	const setAddressInfo = (
		patch: Partial<
			Pick<
				DistrictCreateEditModel,
				"state" | "address" | "city" | "zip" | "emailAddress" | "phone"
			>
		>
	) => setModel((s) => ({ ...s, ...patch }));

	const setSettingsInfo = (
		patch: Partial<Pick<DistrictCreateEditModel, "disableUserImages" | "isIncludedInReports">>
	) => setModel((s) => ({ ...s, ...patch }));

	const hasEmptyRequiredFields = !model.name || !model.code;
	const canSubmit = !isSaving && !hasEmptyRequiredFields;

	const handleSubmit = () => {
		setIsSaving(true);

		if (!hasEmptyRequiredFields) {
			addDistrict(model)
				.unwrap()
				.then((districtId) => {
					// If userIdToAdd is provided (from AddDistrictToUser), add the user to the new district
					if (props.userIdToAdd && districtId) {
						addUserToDistrict({
							districtId: districtId,
							userId: props.userIdToAdd,
						})
							.unwrap()
							.then(() => {
								props.onClose();
							});
					} else {
						props.onClose();
					}
				})
				.catch((error) => {
					setSaveError(error.data);
				});
		}

		setIsSaving(false);
	};

	const handleCancel = () => {
		props.onClose();
	};

	return (
		<section>
			<AlertMessage
				className="my-2"
				type={AlertLevel.Error}
				message={saveError}
				showIcon={true}
				onClear={() => setSaveError("")}
			/>
			<PrimaryDistrictInfoSection
				value={{
					name: model.name,
					code: model.code,
					externalID: model.externalID,
					disableUserImages: model.disableUserImages,
					isIncludedInReports: model.isIncludedInReports,
				}}
				onPrimaryInfoChange={setPrimaryInfo}
				onAddSettingsInfoChange={setSettingsInfo}
			/>
			<DistrictAddressInfoSection
				value={{
					address: model.address,
					city: model.city,
					state: model.state,
					zip: model.zip,
					emailAddress: model.emailAddress,
					phone: model.phone,
				}}
				onAddressChange={setAddressInfo}
				showAddressSection={showAddressSection}
				toggleShowAddressSection={toggleShowAddressSection}
			/>
			<div className="mt-6 grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={!canSubmit}
					onClick={handleSubmit}
					onKeyDown={(e) => handleEnterKey(e, handleSubmit)}
					className="btn btn-primary">
					{props.saveButtonText ?? "Save"}
				</Button>
			</div>
		</section>
	);
}
